import { z } from "zod";
import { EntityProfileIndustry, EntityProfileType } from "../types/entity";
import { TaskPriority, TaskStatus, TaskType } from "../types/task";
import { PersistableViewSchema, ViewSchema } from "../types/views/views";
import { CloseTemplatesSortValue } from "../types/views/closeTemplatesView";
import { CloseTemplateBasis } from "../types/closeTemplateBasis";
import { zQueryUuidArray } from "./query-uuid-array.schema";
import {
    refineViewConfigForCreate,
    viewConfigValueSchema,
} from "./task-template.schema";
export { CloseTemplateBasis } from "../types/closeTemplateBasis";

export const CloseTemplatePathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const CloseTemplateIdPathParamsSchema = z.object({
    closeTemplateId: z.string().uuid(),
});

export const CloseTemplateTypeFilter = z.enum(["general", "client_specific"]);
export type CloseTemplateTypeFilter = z.infer<typeof CloseTemplateTypeFilter>;

// Query strings arrive as "true" / "false". z.coerce.boolean() uses Boolean(),
// and Boolean("false") is true, so an explicit false would include archived rows.
const optionalBooleanQuerySchema = z
    .union([
        z.boolean(),
        z.enum(["true", "false"]).transform((value) => value === "true"),
    ])
    .optional();

export const CloseTemplateListQuerySchema = z.object({
    serviceIds: z.array(z.string().uuid()).max(100).optional(),
    ownerIds: z.array(z.coerce.number().int().positive()).max(100).optional(),
    noTier: optionalBooleanQuerySchema,
    includeArchived: optionalBooleanQuerySchema,
    typeFilter: CloseTemplateTypeFilter.optional(),
    sourceTaskTemplateIds: zQueryUuidArray()
        .pipe(z.array(z.string().uuid()).max(50))
        .optional(),
    // Optional single-column sort. Omitted / cleared falls back to the page
    // default (UPDATED_AT_DESC) resolved in the service.
    sort: z.nativeEnum(CloseTemplatesSortValue).optional(),
});

const closeTemplateBodyShape = {
    serviceId: z.string().uuid().nullable(),
    name: z.string().trim().min(1, { message: "Required" }).max(255),
    ownerId: z.number().int().nullable().optional(),
    basis: z.nativeEnum(CloseTemplateBasis).nullable().optional(),
    industries: z.array(z.nativeEnum(EntityProfileIndustry)).max(50).optional(),
    entityLegalTypes: z
        .array(z.nativeEnum(EntityProfileType))
        .max(50)
        .optional(),
};

// `workspaceId` scopes a template to a single client and is only settable at
// creation time — the update path neither reads nor persists it, so it lives
// on the create schema alone to avoid advertising a field the update contract
// would silently ignore.
export const CloseTemplateCreateSchema = z.object({
    ...closeTemplateBodyShape,
    workspaceId: z.string().uuid().nullable().optional(),
});

export type CloseTemplateCreate = z.infer<typeof CloseTemplateCreateSchema>;

export const CloseTemplateUpdateSchema = z.object({
    ...closeTemplateBodyShape,
    serviceId: z.string().uuid().nullable().optional(),
});

export type CloseTemplateUpdate = z.infer<typeof CloseTemplateUpdateSchema>;

export const CloseTemplateApplySchema = z
    .object({
        workspaceId: z.string().uuid(),
        entityIds: z
            .array(z.number().int())
            .min(1)
            .max(50)
            .refine((ids) => new Set(ids).size === ids.length, {
                message: "Entity IDs must be unique",
            }),
        startDate: z.string().date(),
        endDate: z.string().date(),
        preparerId: z.number().int().nullable().optional(),
        reviewerId: z.number().int().nullable().optional(),
        managerId: z.number().int().nullable().optional(),
        clientId: z.number().int().nullable().optional(),
        workStartDate: z.string().date(),
        workDeliveryDate: z.string().date(),
    })
    .refine(({ startDate, endDate }) => endDate >= startDate, {
        message: "End date must be on or after start date",
        path: ["endDate"],
    })
    .refine(
        ({ workStartDate, workDeliveryDate }) =>
            workDeliveryDate >= workStartDate,
        {
            message: "Work end date must be on or after work start date",
            path: ["workDeliveryDate"],
        },
    );
export type CloseTemplateApply = z.infer<typeof CloseTemplateApplySchema>;

export const CloseTemplateApplyResponseSchema = z.object({
    closesCreated: z.number(),
    tasksCreated: z.number(),
});
export type CloseTemplateApplyResponse = z.infer<
    typeof CloseTemplateApplyResponseSchema
>;

export const CloseTemplatePreviewCloseSchema = z.object({
    id: z.string().uuid(),
    entityId: z.number().int(),
    name: z.string(),
    serviceId: z.string().uuid().nullable(),
    templateId: z.string().uuid(),
    organizationId: z.string().uuid(),
    startDate: z.string().date().nullable(),
    endDate: z.string().date().nullable(),
});

export const CloseTemplatePreviewTaskSchema = z.object({
    id: z.string().uuid(),
    closeId: z.string().uuid(),
    entityId: z.number().int(),
    templateTaskId: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    priority: z.nativeEnum(TaskPriority).nullable(),
    groupId: z.string().uuid().nullable(),
    assigneeId: z.number().int().nullable(),
    externalDeadline: z.number().int().nullable(),
    estimatedTime: z.number().int().nullable(),
    type: z.nativeEnum(TaskType),
    status: z.nativeEnum(TaskStatus),
    dueDate: z.string().date().nullable(),
    closeStageId: z.string().uuid().nullable(),
    view: ViewSchema.optional(),
    skipReason: z.string().nullable().optional(),
    /** Preview task ids that block this task (from template dependencies). */
    blockedByTaskIds: z.array(z.string().uuid()),
    /** Preview task ids related to this task (from template relations). */
    relatedTaskIds: z.array(z.string().uuid()),
});

/**
 * Existing close that already covers part of the previewed period for the
 * same template + entity. The frontend uses this to show a non-blocking
 * overlap warning in the apply wizard's preview step. An overlap is detected
 * when the existing close period intersects the previewed `startDate`/
 * `endDate` (inclusive on both ends).
 */
export const CloseTemplateOverlappingCloseSchema = z.object({
    closeId: z.string().uuid(),
    entityId: z.number().int(),
    startDate: z.string().date().nullable(),
    endDate: z.string().date().nullable(),
    appliedAt: z.string().datetime(),
});
export type CloseTemplateOverlappingClose = z.infer<
    typeof CloseTemplateOverlappingCloseSchema
>;

export const DeleteCloseTemplateResultSchema = z.object({
    isDeleted: z.boolean(),
    isArchived: z.boolean(),
});
export type DeleteCloseTemplateResult = z.infer<
    typeof DeleteCloseTemplateResultSchema
>;

export const ArchiveCloseTemplateResultSchema = z.object({
    id: z.string().uuid(),
    isArchived: z.boolean(),
});
export type ArchiveCloseTemplateResult = z.infer<
    typeof ArchiveCloseTemplateResultSchema
>;

export const CloseTemplatePreviewResponseSchema = z.object({
    closesCreated: z.number(),
    tasksCreated: z.number(),
    closes: z.array(CloseTemplatePreviewCloseSchema),
    tasks: z.array(CloseTemplatePreviewTaskSchema),
    overlappingCloses: z.array(CloseTemplateOverlappingCloseSchema),
});
export type CloseTemplatePreviewResponse = z.infer<
    typeof CloseTemplatePreviewResponseSchema
>;

export const CloseTemplateResolveTaskViewSchema = z
    .object({
        taskType: z.nativeEnum(TaskType),
        viewConfig: viewConfigValueSchema.nullable().optional(),
        entityId: z.number().int().positive(),
        startDate: z.string().date(),
        endDate: z.string().date(),
    })
    .superRefine((data, ctx) =>
        refineViewConfigForCreate(
            { type: data.taskType, viewConfig: data.viewConfig },
            ctx,
        ),
    )
    .refine(({ startDate, endDate }) => endDate >= startDate, {
        message: "End date must be on or after start date",
        path: ["endDate"],
    });
export type CloseTemplateResolveTaskView = z.infer<
    typeof CloseTemplateResolveTaskViewSchema
>;

export const CloseTemplateResolveTaskViewResponseSchema = z.object({
    views: z.array(PersistableViewSchema),
    skipReason: z.string().nullable(),
});
export type CloseTemplateResolveTaskViewResponse = z.infer<
    typeof CloseTemplateResolveTaskViewResponseSchema
>;
